const { ENVIRONMENTS, MARKET_ENVIRONMENTS, ERROR_CODES } = require('../common/constants');
const PalmoError = require('../common/errors');
const EventEmitter = require('../common/EventEmitter');

class PalmoSDK extends EventEmitter {
    /**
     * Initialize Palmo SDK
     * @param {Object} config
     * @param {string} config.apiKey - Your API Key
     * @param {string} [config.environment='production'] - 'production' | 'sandbox' | 'local'
     */
    constructor(config) {
        super();
        if (!config || !config.apiKey) {
            throw new PalmoError(ERROR_CODES.INVALID_CONFIG, 'API Key is required to initialize Palmo SDK.');
        }

        this.apiKey = config.apiKey;

        // Environment Selection
        const envKey = (config.environment || 'production').toUpperCase();
        this.baseUrl = (config.baseUrl || ENVIRONMENTS[envKey] || ENVIRONMENTS.PRODUCTION).replace(/\/+$/, '');
        this.marketBaseUrl = (config.marketBaseUrl || MARKET_ENVIRONMENTS[envKey] || MARKET_ENVIRONMENTS.PRODUCTION).replace(/\/+$/, '');

        // Polling Config
        this.pollingInterval = 2000; // 2s
        this.maxPollingTime = 300000; // 5 mins
    }

    _normalizeScope(scope) {
        const value = scope || ['profile'];
        if (Array.isArray(value)) {
            const scopes = value.filter(item => typeof item === 'string' && item.trim() !== '');
            if (scopes.length === 0) {
                throw new PalmoError(ERROR_CODES.INVALID_CONFIG, 'At least one scope is required.');
            }
            return scopes.join(' ');
        }
        if (typeof value === 'string' && value.trim() !== '') {
            return value.trim();
        }
        throw new PalmoError(ERROR_CODES.INVALID_CONFIG, 'Scope must be a string or an array of strings.');
    }

    _requireAppId(options) {
        const appId = options.appId || options.app_id || this.appId;
        if (!appId) {
            throw new PalmoError(ERROR_CODES.INVALID_CONFIG, 'appId is required for Palmo Connect.');
        }
        return appId;
    }

    /**
     * Internal Fetch Wrapper with Error Handling
     */
    async _request(endpoint, method = 'GET', body = null, extraHeaders = {}, baseUrl = this.baseUrl) {
        try {
            const upperMethod = method.toUpperCase();
            let url = `${baseUrl}${endpoint}`;
            if (upperMethod === 'GET' && body && typeof body === 'object') {
                const params = new URLSearchParams();
                Object.entries(body).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) params.set(key, String(value));
                });
                const query = params.toString();
                if (query) url += `${url.includes('?') ? '&' : '?'}${query}`;
            }
            const options = {
                method: upperMethod,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Api-Key': this.apiKey,
                    ...extraHeaders
                }
            };

            if (body && upperMethod !== 'GET') {
                options.body = JSON.stringify(body);
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                // Try parse error from server
                let errorMsg = 'Unknown API Error';
                try {
                    const errData = await response.json();
                    errorMsg = errData.message || errorMsg;
                } catch (e) { /* ignore */ }

                throw new PalmoError(ERROR_CODES.API_ERROR, `API Request Failed: ${response.status} - ${errorMsg}`);
            }

            if (response.status === 204) return null;
            const text = await response.text();
            return text ? JSON.parse(text) : null;

        } catch (e) {
            if (e instanceof PalmoError) throw e;
            throw new PalmoError(ERROR_CODES.NETWORK_ERROR, e.message, e);
        }
    }

    /**
     * Initiate a "Connect with Palmo" flow.
     * @param {Object} options
     * @param {string[]} [options.scope=['profile']]
     * @returns {Promise<Object>} { request_id, deep_link, qr_data }
     */
    async connect(options = {}) {
        const scope = this._normalizeScope(options.scope);
        const appName = options.appName || 'Palmo Web';
        const response = await this._request('/api/v1/connect/init', 'POST', {
            scope,
            app_name: appName,
            app_id: this._requireAppId(options),
            auth_mode: 'phone_present_qr'
        });
        const result = response && response.data ? response.data : response;
        
        if (result && result.qr_data) {
            this.emit('qr_displayed', result);
        }
        
        return result;
    }

    async connectPalmLogin(options = {}) {
        const scope = this._normalizeScope(options.scope);
        const response = await this._request('/api/v1/connect/palm-login/intent', 'POST', {
            scope,
            app_id: this._requireAppId(options),
            terminal_id: options.terminalId || options.terminal_id,
            terminal_attestation: options.terminalAttestation || options.terminal_attestation
        });
        return response && response.data ? response.data : response;
    }

    _authHeaders(options = {}) {
        const token = options.authToken || options.token || options.bearerToken;
        if (!token) throw new PalmoError(ERROR_CODES.INVALID_CONFIG, 'authToken is required for this endpoint.');
        return { Authorization: `Bearer ${token}` };
    }

    _unwrap(response) {
        return response && response.data ? response.data : response;
    }

    async submitPalmLoginProof(options = {}) { return this._unwrap(await this._request('/api/v1/connect/palm-login/proof', 'POST', {
        request_id: options.requestId || options.request_id, palm_vault_session_id: options.palmVaultSessionId || options.palm_vault_session_id,
        palm_id: options.palmId || options.palm_id, human_root_id: options.humanRootId || options.human_root_id,
        palm_vault_challenge_digest: options.palmVaultChallengeDigest || options.palm_vault_challenge_digest,
        palm_vault_nullifier: options.palmVaultNullifier || options.palm_vault_nullifier, circuit_version: options.circuitVersion || options.circuit_version,
        proof: options.proof, public_signals: options.publicSignals || options.public_signals, capture_evidence: options.captureEvidence || options.capture_evidence,
        terminal_id: options.terminalId || options.terminal_id, terminal_attestation: options.terminalAttestation || options.terminal_attestation
    })); }

    async registerGestureAuthentication(options = {}) { return this._unwrap(await this._request('/api/v1/gesture-auth/register', 'POST', {
        palm_id: options.palmId || options.palm_id, human_root_id: options.humanRootId || options.human_root_id,
        gesture_sequence: options.gestureSequence || options.gesture_sequence
    }, this._authHeaders(options))); }

    async verifyNoPhoneGesture(options = {}) { return this._unwrap(await this._request('/api/v1/pos/gesture-auth/verify', 'POST', {
        human_root_id: options.humanRootId || options.human_root_id, gesture_sequence: options.gestureSequence || options.gesture_sequence, binding: options.binding
    }, this._authHeaders(options))); }

    async createPosTerminal(options = {}) { return this._unwrap(await this._request('/api/v1/pos/terminals', 'POST', {
        terminal_id: options.terminalId || options.terminal_id, label: options.label, limit_grade: options.limitGrade || options.limit_grade,
        max_no_phone_amount: options.maxNoPhoneAmount || options.max_no_phone_amount,
        daily_no_phone_limit: options.dailyNoPhoneLimit || options.daily_no_phone_limit,
        limit_currency: options.limitCurrency || options.limit_currency, capabilities: options.capabilities
    }, this._authHeaders(options))); }

    async listPosTerminals(options = {}) { return this._unwrap(await this._request('/api/v1/pos/terminals', 'GET', null, this._authHeaders(options))); }
    async rotatePosTerminal(options = {}) { return this._unwrap(await this._request(`/api/v1/pos/terminals/${encodeURIComponent(options.terminalId || options.terminal_id)}/rotate`, 'POST', null, this._authHeaders(options))); }
    async revokePosTerminal(options = {}) { return this._unwrap(await this._request(`/api/v1/pos/terminals/${encodeURIComponent(options.terminalId || options.terminal_id)}`, 'DELETE', null, this._authHeaders(options))); }

    async createPalmPosPaymentIntent(options = {}) { return this._unwrap(await this._request('/api/v1/pos/palm-payment/intent', 'POST', {
        amount: options.amount, crypto_asset_symbol: options.cryptoAssetSymbol || options.crypto_asset_symbol, network: options.network,
        destination_address: options.destinationAddress || options.destination_address, pos_terminal_id: options.posTerminalId || options.pos_terminal_id || options.terminalId,
        terminal_attestation: options.terminalAttestation || options.terminal_attestation
    }, this._authHeaders(options))); }

    async submitPalmPosPaymentProof(options = {}) { return this._unwrap(await this._request('/api/v1/pos/palm-payment/proof', 'POST', {
        payment_intent_id: options.paymentIntentId || options.payment_intent_id, palm_vault_session_id: options.palmVaultSessionId || options.palm_vault_session_id,
        palm_id: options.palmId || options.palm_id, human_root_id: options.humanRootId || options.human_root_id, account_address: options.accountAddress || options.account_address,
        gesture_proof_digest: options.gestureProofDigest || options.gesture_proof_digest, palm_vault_challenge_digest: options.palmVaultChallengeDigest || options.palm_vault_challenge_digest,
        palm_vault_nullifier: options.palmVaultNullifier || options.palm_vault_nullifier, circuit_version: options.circuitVersion || options.circuit_version,
        proof: options.proof, public_signals: options.publicSignals || options.public_signals, capture_evidence: options.captureEvidence || options.capture_evidence,
        proof_mode: options.proofMode || options.proof_mode, pos_terminal_id: options.posTerminalId || options.pos_terminal_id || options.terminalId,
        terminal_attestation: options.terminalAttestation || options.terminal_attestation
    }, this._authHeaders(options))); }

    /**
     * Initiate a Payment Request.
     * @param {Object} details { amount, currency, description, orderId }
     * @returns {Promise<Object>} { request_id, deep_link, qr_data }
     */
    async pay(details) {
        if (!details.amount || !details.currency) {
            throw new PalmoError(ERROR_CODES.INVALID_CONFIG, 'Amount and Currency are required for payment.');
        }
        const response = await this._request('/api/v1/connect/pay/init', 'POST', details);
        const result = response && response.data ? response.data : response;

        if (result && !result.qr_data && result.qr_code_data) {
            result.qr_data = result.qr_code_data;
        }
        
        if (result && result.qr_data) {
            this.emit('qr_displayed', result);
        }
        
        return result;
    }

    /**
     * Poll status of an operation until completion or timeout.
     * @param {string} id - Session ID or Payment ID (request_id)
     * @param {Function} [onStatusChange] - Callback for intermediate status updates
     * @returns {Promise<Object>} Final result
     */
    async waitForStatus(id, onStatusChange, endpointPrefix = '/api/v1/connect/status') {
        const startTime = Date.now();

        return new Promise((resolve, reject) => {
            const poll = async () => {
                try {
                    // Check timeout
                    if (Date.now() - startTime > this.maxPollingTime) {
                        reject(new PalmoError(ERROR_CODES.AUTH_TIMEOUT, 'Operation timed out'));
                        return;
                    }

                    const result = await this._request(`${endpointPrefix}/${id}`, 'GET');

                    if (onStatusChange) {
                        onStatusChange(result.status);
                    }
                    this.emit('status_changed', result.status);

                    switch (result.status) {
                        case 'approved':
                        case 'confirmed':
                        case 'success':
                        case 'completed':
                            this.emit('auth_success', result);
                            resolve(result);
                            break;

                        case 'failed':
                        case 'declined':
                        case 'expired':
                            this.emit('auth_failed', result);
                            reject(new PalmoError(ERROR_CODES.AUTH_FAILED, `Operation ${result.status}`));
                            break;

                        default:
                            setTimeout(poll, this.pollingInterval);
                            break;
                    }
                } catch (error) {
                    reject(error);
                }
            };

            poll();
        });
    }

    /**
     * Alias for waitForStatus (Connect flow)
     * @param {string} sessionId 
     */
    async waitForAuth(sessionId) {
        return this.waitForStatus(sessionId);
    }

    async waitForPaymentStatus(paymentId, onStatusChange) {
        return this.waitForStatus(paymentId, onStatusChange, '/api/v1/payment/status');
    }
}

module.exports = PalmoSDK;
